import { ChangeEvent, forwardRef, ForwardRefRenderFunction, useCallback, KeyboardEvent, ReactNode, useState, useMemo } from 'react';
import { Button, StandardTextFieldProps, TextField } from '@mui/material';
import { InputLabel, TextFieldWrapper, TextInputWrapper } from '@styles/InputStyles';
import { LightTooltip } from '@shared/components/grid/GridCell';
import { HiddenInput } from '../styledComponents';

export interface ITextInputOwnProps {
    shrink?: boolean;
    required?: boolean;
    displayError?: boolean;
    autofill?: boolean;
    pattern?: string;
    endAdornment?: React.ReactNode;
}

export interface ITextInputProps extends ITextInputOwnProps, StandardTextFieldProps {
    highlightFullBorder?: boolean;
    unStyled?: boolean;
    isNotNumeric?: boolean;
    isAbleToCall?: boolean;
    isMultipleZipInput?: boolean;
    enableButton?: boolean;
    buttonText?: string,
    showTooltip?: boolean,
    maxLen?: number;
    onBtnClick?:  React.MouseEventHandler<HTMLButtonElement>;
    InputWrapper?: React.ElementType;
}

type TKeyDownEvent = ChangeEvent<HTMLInputElement> & KeyboardEvent;

const Wrapper = ({ unStyled, withLabel, children }: { unStyled: boolean; withLabel: boolean; children: ReactNode }) => {
    if (unStyled) {
        return <div>{children}</div>;
    }

    return <TextInputWrapper withLabel={withLabel}>{children}</TextInputWrapper>;
};

const initZipCharsLength = 4;
const initZipCharsLengthPlusHyphen = 5;
const fullZipLength = 9;
const fullZipLengthPlusComa = 10;

const TextInputBase: ForwardRefRenderFunction<HTMLDivElement, ITextInputProps> = (
    {
        value,
        error,
        type = 'text',
        label = '',
        buttonText= '',
        shrink = true,
        autofill = false,
        fullWidth = true,
        displayError = false,
        isNotNumeric = false,
        isMultipleZipInput = false,
        highlightFullBorder = false,
        enableButton = false,
        pattern,
        required,
        helperText,
        onBtnClick,
        onChange,
        onKeyDown,
        unStyled,
        InputWrapper,
        maxLen = 10,
        showTooltip = false,
        ...props
    },
    ref,
) => {
    const valueIsMissing = !value && value !== 0;
    const errorRequired = displayError && required && valueIsMissing;
    const errorRequiredText = errorRequired && `${label} is required`;
    const GeneralInputWrapper = InputWrapper ? InputWrapper : Wrapper;
    const [isFocused, setIsFocused] = useState(false);

    const enableTootlTip = useMemo(() => showTooltip && ((value || '') as string).length > maxLen, [maxLen, value, showTooltip]);
    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!onChange) return;

            const { value: val } = event.target;

            if ((isNotNumeric && !val.match(/^[^\d]*$/)) || (pattern && !val.match(new RegExp(pattern, 'g')))) {
                return;
            }

            if (isMultipleZipInput) {
                const zipCodes = val.split(', ').map((zip) => zip.trim());
                const lastZipCode = zipCodes[zipCodes.length - 1] ?? zipCodes[0] ?? '';
                const lastZipCorrectChars: string[] = [];

                lastZipCode.split('').map((char, index) => {
                    if (
                        (index <= initZipCharsLength || (index > initZipCharsLengthPlusHyphen && index <= fullZipLength)) &&
                        /\d/.test(char)
                    ) {
                        lastZipCorrectChars.push(char);
                    } else if ((index === initZipCharsLengthPlusHyphen && char === ',') || index === fullZipLengthPlusComa) {
                        lastZipCorrectChars.push(', ');
                    } else if (index === initZipCharsLengthPlusHyphen && char !== ',') {
                        lastZipCorrectChars.push('-');
                    } else {
                        return;
                    }
                });

                zipCodes.pop();
                zipCodes.push(lastZipCorrectChars.join(''));

                onChange({ ...event, target: { ...event.target, value: zipCodes.join(', ') } });
            } else {
                onChange(event);
            }
        },
        [onChange, pattern, isNotNumeric],
    );

    const handleKeyDown = useCallback(
        (event: TKeyDownEvent) => {
            // First call the external onKeyDown if provided
            onKeyDown?.(event);

            if (type === 'number' && ['e', 'E', '-', '+', ','].includes(event.key)) {
                event.preventDefault();
            }

            if (
                isMultipleZipInput &&
                event.key === 'Backspace' &&
                event.target?.value[event.target.value.length - 2] === ','
            ) {
                onChange?.({ ...event, target: { ...event.target, value: event.target.value.slice(0, -1) } });
            }
        },
        [onChange, isMultipleZipInput, type,onKeyDown],
    );

    return (
        <GeneralInputWrapper unStyled={!!unStyled} withLabel={!!label}>
            {label &&
                <InputLabel>
                    {label}
                </InputLabel>
            }
            {enableButton &&
                <InputLabel top="-19.5%" left="33px">
                    <Button
                        color='info'
                        variant='text'
                        size='small'
                        sx={{ padding: '0 5px 2px 3px' }}
                        onClick={onBtnClick}>
                        {buttonText}
                    </Button>
                </InputLabel>
            }
            {!autofill && <HiddenInput type={type} name={label as string} tabIndex={-1} />}
            <TextFieldWrapper highlightFullBorder={highlightFullBorder}>
                {enableTootlTip ?
                    <LightTooltip title={(!isFocused ? value : '') as string} placement="top" >
                        <TextField
                            {...props}
                            inputRef={ref}
                            type={type}
                            value={isFocused ? value ?? '' : `${String(value ?? '').slice(0, maxLen)}...`}
                            error={error || errorRequired}
                            helperText={helperText || errorRequiredText}
                            required={required}
                            fullWidth={fullWidth}
                            data-error={error}
                            variant="outlined"
                            InputLabelProps={{ shrink, tabIndex: -1 }}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            onKeyDown={handleKeyDown}
                            onChange={handleChange}
                        />
                    </LightTooltip>
                    :
                    <TextField
                        {...props}
                        inputRef={ref}
                        type={type}
                        value={value ?? ''}
                        error={error || errorRequired}
                        helperText={helperText || errorRequiredText}
                        required={required}
                        fullWidth={fullWidth}
                        data-error={error}
                        variant="outlined"
                        InputLabelProps={{ shrink, tabIndex: -1 }}
                        onKeyDown={handleKeyDown}
                        onChange={handleChange}
                    />
                }
            </TextFieldWrapper>
        </GeneralInputWrapper>
    );
};

export const TextInput = forwardRef(TextInputBase);
